import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import { useCompany } from "../../components/context/CompanyContext";
import CustomGrid from "../../components/grid/CustomGrid";
import Loading from "../../components/loading/loading";
import { titleSetRef } from "../../redux/titleRedux";
import { userRequest, generateFileUrl } from "../../requestMethods";
import { withRowTotal } from "../../util/sizeTotal";
import "../../App.css";

const BRAND = "#EE8253";

const BASE_COLUMNS = [
  {
    field: "avatar",
    type: "text",
    headerName: "Foto",
    align: "center",
    headerAlign: "center",
    width: 50,
    renderCell: (params) => (
      <Avatar sx={{ width: "30px", height: "30px" }} src={generateFileUrl(params.value)} />
    ),
  },
  { field: "product", type: "text", headerName: "Referência", width: 150, align: "center", headerAlign: "center" },
  {
    field: "color",
    type: "text",
    headerName: "Cor",
    width: 60,
    align: "center",
    headerAlign: "center",
    renderCell: (params) => (
      <Box sx={{ height: "30px", width: "30px", backgroundColor: params.value, borderRadius: "5px" }} />
    ),
  },
  { field: "colorRef", type: "text", headerName: "", width: 50, align: "center", headerAlign: "center" },
  // Soma de todos os itens da linha — calculado no load (withRowTotal), pois o
  // CustomGrid não repassa valueGetter.
  { field: "total", type: "number", headerName: "Total", width: 70, align: "center", headerAlign: "center" },
];

// Tela dedicada "Produtos com Venda" — só a tabela de produtos vendidos, layout novo.
export default function RelatorioProdutoVenda() {
  const dispatch = useDispatch();
  const nav = useNavigate();
  const { companyId, slug } = useCompany();

  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState(BASE_COLUMNS);
  const [isLoading, setIsLoading] = useState(true);
  const [readonly, setReadonly] = useState("7");
  const [dataIni, setDateIni] = useState(() => dayjs().add(-7, "day").format("YYYY-MM-DD"));
  const [dataFim, setDateFim] = useState(() => dayjs().format("YYYY-MM-DD"));

  useEffect(() => {
    dispatch(titleSetRef({ title: "Produtos com Venda" }));
    window.scrollTo(0, 0);
  }, [dispatch]);

  useEffect(() => {
    if (companyId > 0) getSizes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    if (companyId > 0) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, dataIni, dataFim]);

  const getSizes = async () => {
    try {
      const res = await userRequest.get(`${companyId}/products/sizes`);
      const sorted = [...res.data].sort(
        (a, b) => (a.order ?? 9999) - (b.order ?? 9999) || a.id - b.id,
      );
      const sizeCols = sorted.map((item) => ({
        field: item.label,
        headerName: item.label,
        width: 50,
        align: "center",
        headerAlign: "center",
      }));
      setColumns([...BASE_COLUMNS, ...sizeCols]);
    } catch {}
  };

  const load = async () => {
    setIsLoading(true);
    try {
      const res = await userRequest.post(`${companyId}/reportOrders/salesProductOrders`, {
        start: dataIni,
        end: dataFim,
      });
      setRows(withRowTotal(res.data));
    } catch {
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const v = e.target.value;
    setReadonly(v);
    const map = { "0": 0, "7": 7, "15": 15, "30": 30 };
    if (v in map) {
      setDateIni(dayjs().add(-map[v], "day").format("YYYY-MM-DD"));
      setDateFim(dayjs().format("YYYY-MM-DD"));
    }
  };

  const periodLabel =
    readonly === "F" ? "filtro personalizado" : readonly === "0" ? "hoje" : `últimos ${readonly} dias`;

  return (
    <div style={{ padding: "16px 16px 48px 4px", fontFamily: "Manrope", background: "#F9FAFB", minHeight: "100vh" }}>
      <div
        onClick={() => nav(`/${slug}/indicadoresprodutos`)}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 12, cursor: "pointer",
          color: "#6B7280", fontSize: 13, fontWeight: 600, fontFamily: "Manrope",
          padding: "6px 12px", borderRadius: 8, border: "1px solid #E5E7EB",
          background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
        }}
      >
        ← Voltar para Indicadores
      </div>

      {/* Header + filtro período */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20, justifyContent: "space-between" }}>
        <div>
          <span style={{ fontSize: 18, fontWeight: 700, color: "#111827" }}>Produtos com Venda</span>
          <span style={{ fontSize: 12, color: "#9CA3AF", marginLeft: 8 }}>{periodLabel}</span>
        </div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 4 }}>Período</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", border: "1px solid #E5E7EB", borderRadius: 8, padding: "6px 8px", background: "#fff" }}>
            {[
              { v: "30", l: "30 dias" },
              { v: "15", l: "15 dias" },
              { v: "7", l: "7 dias" },
              { v: "0", l: "Hoje" },
              { v: "F", l: "Filtro" },
            ].map(({ v, l }) => (
              <label
                key={v}
                style={{
                  cursor: "pointer", padding: "3px 10px", borderRadius: 6,
                  border: `1px solid ${readonly === v ? BRAND : "#E5E7EB"}`,
                  background: readonly === v ? BRAND : "transparent",
                  color: readonly === v ? "#fff" : "#6B7280", fontSize: 12, fontWeight: 600,
                }}
              >
                <input type="radio" value={v} checked={readonly === v} onChange={handleChange} style={{ display: "none" }} />
                {l}
              </label>
            ))}
            {readonly === "F" && (
              <>
                <div style={{ width: 1, background: "#E5E7EB", margin: "2px 2px" }} />
                <input type="date" value={dataIni} onChange={(e) => setDateIni(e.target.value)} min="2023-01-01" style={{ fontSize: 12, border: "none", outline: "none" }} />
                <span style={{ color: "#9CA3AF", fontSize: 12 }}>—</span>
                <input type="date" value={dataFim} onChange={(e) => setDateFim(e.target.value)} min="2023-01-01" style={{ fontSize: 12, border: "none", outline: "none" }} />
              </>
            )}
          </div>
        </div>
      </div>

      {isLoading ? (
        <Loading />
      ) : (
        <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #E5E7EB", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", padding: "16px" }}>
          <div style={{ height: "calc(100vh - 230px)", width: "100%" }}>
            <CustomGrid
              gridId="relatorioProdutoVenda"
              rows={rows}
              columns={columns}
              pageSize={45}
              checkboxSelection={false}
            />
          </div>
        </div>
      )}
    </div>
  );
}
